<script lang="ts">
  import type { Store } from '../state/store.svelte';
  import type { GraphicsProfile, ScaleMode } from '../state/selectors';
  import { selectDiagnostics, selectScaleMode } from '../state/selectors';
  import { writeGraphicsProfile, writeReducedEffects } from '../persist/graphics-prefs';
  import { startFpsSampler } from './diagnostics-fps';

  // Settings surface (Story 1.6): graphics-profile selector, reduced-effects
  // toggle, and a diagnostics view. `ui` depends on `state` only: it reads plain
  // view data via `selectDiagnostics` and dispatches commands; the graphics prefs
  // are persisted through `persist` (never `state`/`core`). Every control is
  // keyboard-operable with visible focus and a programmatic label; state is never
  // signalled by colour alone (radio/checkbox semantics + text chips).
  let {
    store,
    announce
  }: { store: Store; announce?: (message: string) => void } = $props();

  const diagnostics = $derived(selectDiagnostics(store.state));
  const scaleMode = $derived(selectScaleMode(store.state));

  const PROFILES: { value: GraphicsProfile; label: string; hint: string }[] = [
    { value: 'auto', label: 'Auto', hint: 'Pick the safest suitable profile automatically' },
    { value: 'enhanced', label: 'Enhanced', hint: 'WebGL particles, shadows and lighting when available' },
    { value: 'compatibility', label: 'Compatibility', hint: 'Canvas 2D baseline; works everywhere' }
  ];

  // Story 3.3 body-scale control. GCSE-level helper copy explains the trade-off.
  const SCALE_MODES: { value: ScaleMode; label: string; hint: string }[] = [
    {
      value: 'readable',
      label: 'Readable',
      hint: 'Bodies enlarged so they are easy to see and click'
    },
    {
      value: 'realistic',
      label: 'Realistic',
      hint: 'True relative sizes; planets look almost invisibly tiny against the huge distances between them'
    }
  ];

  const MODE_LABEL: Record<'enhanced' | 'compatibility', string> = {
    enhanced: 'Enhanced (WebGL)',
    compatibility: 'Compatibility (Canvas 2D)'
  };

  // Enhanced was asked for but is not live: explain the active Compatibility mode
  // without ever claiming Enhanced is active (AC 5).
  const enhancedUnavailable = $derived(
    diagnostics.requestedProfile === 'enhanced' &&
      diagnostics.activeRenderMode === 'compatibility'
  );

  function chooseProfile(profile: GraphicsProfile): void {
    if (profile === diagnostics.requestedProfile) return;
    store.dispatch({ type: 'SetGraphicsProfile', profile });
    writeGraphicsProfile(profile);
    announce?.(`Graphics profile set to ${profile}.`);
  }

  function chooseScaleMode(mode: ScaleMode): void {
    // Shareable experiment config (AD-4), NOT a device preference: dispatch ONLY,
    // never persist (no `persist` writer). Story 5.1 will pick up the store field.
    if (mode === scaleMode) return;
    store.dispatch({ type: 'SetScaleMode', mode });
    announce?.(`Body scale set to ${mode}.`);
  }

  function toggleReducedEffects(event: Event): void {
    const value = (event.currentTarget as HTMLInputElement).checked;
    store.dispatch({ type: 'SetReducedEffects', value });
    writeReducedEffects(value);
    announce?.(value ? 'Reduced effects on.' : 'Reduced effects off.');
  }

  // Ephemeral frame-rate reading (never command-driven state, AD-2). Runs only
  // while the panel is open; started/stopped with this component's lifecycle.
  let fps = $state(0);
  $effect(() => {
    const sampler = startFpsSampler((value) => {
      fps = value;
    });
    return () => sampler.stop();
  });
</script>

<div
  class="settings-panel glass-panel"
  role="dialog"
  aria-label="Graphics settings and diagnostics"
  tabindex="-1"
  data-testid="settings-panel"
>
  <h2 class="settings-title">Graphics</h2>

  <fieldset class="profile-group">
    <legend class="group-label">Graphics profile</legend>
    {#each PROFILES as profile (profile.value)}
      <label class="profile-option focus-ring">
        <input
          type="radio"
          name="graphics-profile"
          value={profile.value}
          checked={diagnostics.requestedProfile === profile.value}
          data-testid={`profile-${profile.value}`}
          onchange={() => chooseProfile(profile.value)}
        />
        <span class="profile-text">
          <span class="profile-name">{profile.label}</span>
          <span class="profile-hint">{profile.hint}</span>
        </span>
      </label>
    {/each}
  </fieldset>

  <label class="reduced-effects focus-ring">
    <input
      type="checkbox"
      checked={diagnostics.reducedEffects}
      data-testid="reduced-effects-toggle"
      onchange={toggleReducedEffects}
    />
    <span>
      <span class="profile-name">Reduced effects</span>
      <span class="profile-hint">Limit particles, shadows and transitions</span>
    </span>
  </label>

  <h2 class="settings-title">Body scale</h2>
  <fieldset class="profile-group">
    <legend class="group-label">Body scale</legend>
    {#each SCALE_MODES as mode (mode.value)}
      <label class="profile-option focus-ring">
        <input
          type="radio"
          name="scale-mode"
          value={mode.value}
          checked={scaleMode === mode.value}
          data-testid={`scale-mode-${mode.value}`}
          onchange={() => chooseScaleMode(mode.value)}
        />
        <span class="profile-text">
          <span class="profile-name">{mode.label}</span>
          <span class="profile-hint">{mode.hint}</span>
        </span>
      </label>
    {/each}
  </fieldset>

  <h2 class="settings-title">Diagnostics</h2>
  <dl class="diagnostics" data-testid="diagnostics">
    <div class="diag-row">
      <dt>Active mode</dt>
      <dd data-testid="diag-active-mode">{MODE_LABEL[diagnostics.activeRenderMode]}</dd>
    </div>
    <div class="diag-row">
      <dt>Requested</dt>
      <dd data-testid="diag-requested">{diagnostics.requestedProfile}</dd>
    </div>
    <div class="diag-row">
      <dt>Frame rate</dt>
      <dd class="data-mono" data-testid="diag-fps">{fps} fps</dd>
    </div>
    <div class="diag-row">
      <dt>Unavailable features</dt>
      <dd data-testid="diag-unavailable">
        {diagnostics.unavailableFeatures.length === 0
          ? 'None'
          : diagnostics.unavailableFeatures.join(', ')}
      </dd>
    </div>
  </dl>

  {#if enhancedUnavailable}
    <p class="fallback-note" data-testid="enhanced-unavailable-note">
      Enhanced graphics are not available on this device, so Compatibility mode is
      active. Every control, reading, and calculation works exactly the same.
    </p>
  {/if}
</div>

<style>
  .settings-panel {
    position: absolute;
    top: calc(var(--top-bar-height) + 12px);
    right: var(--edge-margin);
    width: 320px;
    max-width: calc(100vw - 2 * var(--edge-margin));
    max-height: calc(100vh - var(--top-bar-height) - 24px);
    overflow-y: auto;
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    border-radius: var(--radius-xl);
    z-index: 20;
  }

  .settings-title {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--on-surface-variant);
  }

  .profile-group {
    border: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .group-label {
    padding: 0;
    font-size: 11px;
    color: var(--on-surface-variant);
  }

  .profile-option,
  .reduced-effects {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 8px;
    border-radius: var(--radius-default);
    cursor: pointer;
  }

  .profile-option:hover,
  .reduced-effects:hover {
    background: rgba(255, 255, 255, 0.06);
  }

  .profile-text,
  .reduced-effects span:last-child {
    display: flex;
    flex-direction: column;
  }

  .profile-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--on-surface);
  }

  .profile-hint {
    font-size: 11px;
    color: var(--on-surface-variant);
  }

  .diagnostics {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .diag-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
  }

  .diag-row dt {
    color: var(--on-surface-variant);
  }

  .diag-row dd {
    margin: 0;
    color: var(--on-surface);
    text-align: right;
  }

  .fallback-note {
    margin: 0;
    padding: 10px;
    font-size: 12px;
    line-height: 1.4;
    color: var(--on-surface);
    background: rgba(255, 255, 255, 0.06);
    border-radius: var(--radius-default);
  }

  input[type='radio'],
  input[type='checkbox'] {
    margin-top: 2px;
    accent-color: var(--primary);
  }
</style>
